import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Upload,
  Settings,
  LogOut,
  Menu,
  X,
  Video,
} from 'lucide-react'
import { useAuthStore } from '../../stores/auth'

interface NavItem {
  label: string
  to: string
  icon: React.ReactNode
  adminOnly?: boolean
}

const navItems: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard', icon: <LayoutDashboard size={18} /> },
  { label: 'New Upload', to: '/upload', icon: <Upload size={18} /> },
  { label: 'Admin', to: '/admin', icon: <Settings size={18} />, adminOnly: true },
]

export function Sidebar() {
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  const isAdmin = user?.role === 'admin'

  const SidebarContent = () => (
    <div className="flex flex-col h-full bg-alk-black text-white">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-4 py-5 border-b border-white/10">
        <div className="w-8 h-8 bg-alk-red flex items-center justify-center flex-shrink-0">
          <Video size={16} className="text-white" />
        </div>
        <div>
          <div className="font-display font-bold text-sm uppercase tracking-widest text-white leading-tight">
            VideoLab
          </div>
          <div className="text-xs text-alk-gray leading-tight">Alchatek</div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 space-y-0.5 overflow-y-auto scrollbar-thin">
        <div className="section-label px-3 pb-2">Navigation</div>
        {navItems
          .filter((item) => !item.adminOnly || isAdmin)
          .map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                isActive ? 'sidebar-nav-item-active' : 'sidebar-nav-item'
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
      </nav>

      {/* User / Logout */}
      <div className="border-t border-white/10 p-3">
        {user && (
          <div className="px-3 py-2 mb-1">
            <div className="text-xs text-alk-gray truncate">{user.email}</div>
            <div className="text-xs font-display uppercase tracking-wide text-white/50 mt-0.5">
              {user.role}
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="sidebar-nav-item w-full text-left"
        >
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  )

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-56 flex-shrink-0 h-screen sticky top-0">
        <SidebarContent />
      </aside>

      {/* Mobile toggle button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 w-9 h-9 bg-alk-black text-white flex items-center justify-center"
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-label="Toggle menu"
      >
        {mobileOpen ? <X size={18} /> : <Menu size={18} />}
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/50"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={`lg:hidden fixed top-0 left-0 z-40 w-56 h-full transition-transform duration-200 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <SidebarContent />
      </aside>
    </>
  )
}
